import React, { useEffect, useState, useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { Collapse, Tag } from 'antd';
import styles from './ProjectCheckerViewer.module.less';
import { selectProject } from "../tuzhi-vis/tuzhiVisSlice";
import { typeList } from 'antd/lib/message';

interface ProjectCheckerViewerProps {
  projectId: number;
}

const CHECK_ITEM_MAP: { [key: string]: string } = {
  '是否为空图': '空图纸',
  '是否受损': '文件受损',
  '是否为多页文件': '多页文件',
  '是否为扫描图': '扫描图',
  '是否为彩图': '彩图',
  '是否存在文本乱码': '文本乱码',
  '是否为多专业图纸': '多专业图纸',
  '是否存在非标图例': '存在非标图例',
  '缺少轴网': '缺少轴网',
}

const { Panel } = Collapse;

function ProjectCheckerViewer(props: ProjectCheckerViewerProps) {
  const project = useAppSelector(selectProject);
  console.log("[debug] 项目属性信息");
  console.log(project?.properties);

  const [data, setData] = useState<any>(null);
  const [currentUseFloors, setCurrentUseFloors] = useState<any>(null);

  useEffect(() => {
    if (!project || !Array.isArray(project.properties)) return;

    const item = project.properties.find(
      (entry: any) => entry.key === "TZ2.质量检查结果"
    );

    const floorItem = project.properties.find(
      (entry: any) => entry.key === "开业前检查申请表.使用层数"
    );

    if (item?.value) {
      try {
        const parsed = JSON.parse(String(item.value));
        setData(parsed);
        console.log("[debug] 解析成功", parsed);
      } catch (err) {
        console.error("[error] JSON 解析失败：", err);
      }
    } else {
      console.warn("[warn] 找不到预检查结果字段");
    }

    if (floorItem?.value) {
      setCurrentUseFloors(floorItem.value);
      console.log("[debug] 申请表楼层：",floorItem.value)
    } else {
      console.warn("[warn] 找不到 开业前检查申请表.使用层数 字段");
    }


  }, [project]);

const intraCheckProblems = useMemo(() => {
    const problemsByCheckType: { [key: string]: any[] } = {};

    // 初始化，确保所有检查项都会显示，即使问题数量为0
    Object.keys(CHECK_ITEM_MAP).forEach(key => {
        problemsByCheckType[key] = [];
    });

    const projectsData = data?.CheckResult?.['项目图纸'] || {};
    if (!data) return problemsByCheckType;

    // 遍历原始数据，将问题文件归类
    for (const projectName in projectsData) {
        const folders = projectsData[projectName];
        for (const folderName in folders) {
            const files = folders[folderName];
            for (const fileName in files) {
                const fileData = files[fileName];
                const intraChecks = fileData?.intra_check || {};
                for (const checkKey in intraChecks) {
                    if (Object.hasOwn(problemsByCheckType, checkKey)) {
                      const value = intraChecks[checkKey];

                      // 如果是 0、false、null、undefined，则跳过
                      if (!value) continue;

                      // 如果是字符串，则保存描述信息
                      if (typeof value === 'string') {
                        problemsByCheckType[checkKey].push({
                          fileName,
                          filePath: fileData.文件路径,
                          profession: folderName,
                          detail: value,  // 保存缺失说明
                        });
                      } 
                      // 如果是数字（如 1），仍按旧逻辑处理
                      else if (value === 1) {
                        problemsByCheckType[checkKey].push({
                          fileName,
                          filePath: fileData.文件路径,
                          profession: folderName,
                        });
                      }
                    }
                }
            }
        }
    }
    return problemsByCheckType;
}, [data]);

  if (data == null) {
    return (
      <div>
        <div className={styles.bigFont}>TODO</div>
      </div>
    );
  }

  // const { project_name, score, level, deduction_detail } = data;
  const checkResult = data.CheckResult || {};

  // === 各类数据提取 ===
  const type1List =
    checkResult['无法解析的图纸']?.['TYPE1_专业和图纸类型都无法解析'] || [];
  const type2List =
    checkResult['无法解析的图纸']?.['TYPE2_只有专业无法解析'] || [];
  const type3List =
    checkResult['无法解析的图纸']?.['TYPE3_只有图纸类型无法解析'] || [];

  const faultedNameLength = type1List.length + type2List.length + type3List.length;

  const misplacedList = checkResult['错放的图纸'] || [];
  const redundantList = checkResult['多余的图纸'] || [];
  const duplicateList = checkResult['重复的图纸'] || [];
  const missingList = checkResult['缺失的专业类型'] || [];
  const repeatedProfessionList = checkResult['重复的专业类型'] || [];
  const missingFiles = checkResult['缺失的图纸'] || [];

  const formatPath = (fullPath: string | undefined): string => {
    {/* 去除无用文件夹路径 */}
    if (!fullPath) {
      return "路径不存在";
    }
    const parts = fullPath.split(/[\\/]/);
    return parts.slice(-1).join('\\');
  };

  return (
    <div className={styles.root}>
      <div className={styles.scrollContainer}>
        <div className={styles.bigFont}>项目质量预审查结果</div>

        <Collapse defaultActiveKey={['summary']}>

          {/* 文件间质量检查 */}
          <Panel header="文件间质量检查 " key="inter">
          
            <Collapse>
              {/* 命名不规范 */}
              {/* <Panel header="命名不规范图纸" key="naming"> */}
              <Panel header={`命名不规范图纸 （问题图纸数量： ${faultedNameLength} 个）`} key="inter">
                <Collapse>
                  <Panel
                    header={`缺少专业、图纸类型信息（${type1List.length} 个）`}
                    key="naming-type1"
                  >
                    {type1List.length === 0 ? (
                      <div className={styles.smallFont}>✅ 无此类问题图纸</div>
                    ) : (
                      type1List.map(
                        ([project, folder, name, floors]: any, idx: number) => (
                          <div className={styles.smallFont} key={idx}>
                            {/* <Tag color="orange">{folder}</Tag> / {name}（楼层： */}
                            {folder}/{name}
                          </div>
                        )
                      )
                    )}
                  </Panel>

                  <Panel
                    header={`缺少专业类型信息（${type2List.length} 个）`}
                    key="naming-type2"
                  >
                    {type2List.length === 0 ? (
                      <div className={styles.smallFont}>✅ 无此类问题图纸</div>
                    ) : (
                      type2List.map(
                        (
                          [project, folder, name, floors, drawType]: any,
                          idx: number
                        ) => (
                          <div className={styles.smallFont} key={idx}>
                            {/* <Tag color="orange">{folder}</Tag> / {name}（图纸类型： */}
                            {folder}/{name}
                          </div>
                        )
                      )
                    )}
                  </Panel>

                  <Panel
                    header={`缺少图纸类型信息（${type3List.length} 个）`}
                    key="naming-type3"
                  >
                    {type3List.length === 0 ? (
                      <div className={styles.smallFont}>✅ 无此类问题图纸</div>
                    ) : (
                      type3List.map(
                        (
                          [project, folder, name, floors, profession]: any,
                          idx: number
                        ) => (
                          <div className={styles.smallFont} key={idx}>
                            {/* <Tag color="orange">{folder}</Tag> / {name}（专业： */}
                            {folder}/{name}
                          </div>
                        )
                      )
                    )}
                  </Panel>
                </Collapse>
              </Panel>

              {/* 错放图纸 */}
              <Panel
                header={`错放图纸（${misplacedList.length} 项）`}
                key="misplaced"
              >
                {misplacedList.length === 0 ? (
                  <div className={styles.smallFont}>✅ 无错放图纸</div>
                ) : (
                  misplacedList.map(
                    ([project, folder, file, expected]: any, idx: number) => (
                      <div className={styles.smallFont} key={idx}>
                        当前位置：<Tag color="red">{folder}</Tag>/{file},{' '}
                        <Tag color="blue">应放置专业：{expected}</Tag> 
                      </div>
                    )
                  )
                )}
              </Panel>

              {/* 重复图纸 */}
              <Panel
                header={`重复图纸（${duplicateList.length} 组）`}
                key="duplicate"
              >
                {duplicateList.length === 0 ? (
                  <div className={styles.smallFont}>✅ 无重复图纸</div>
                ) : (
                  duplicateList.map((group: any[], idx: number) => (
                    <div key={idx} style={{ marginBottom: '12px' }}>
                      <div className={styles.middleFont}>
                        <Tag color="blue">重复组 {idx + 1}</Tag>（共{' '}
                        {group.length} 份）
                      </div>
                      <ul
                        className={styles.smallFont}
                        style={{ paddingLeft: '1.5em' }}
                      >
                        {group.map((file: any, i: number) => (
                          <li key={i}>
                            路径：{file.文件路径} 
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </Panel>


              {/* 多余图纸 */}
              <Panel
                header={`多余图纸（${redundantList.length} 项） 【当前装修楼层: ${currentUseFloors}】`}
                key="redundant"
              >
                {redundantList.length === 0 ? (
                  <div className={styles.smallFont}>✅ 无多余图纸</div>
                ) : (
                  <div className={`${styles.problemListGrid} ${styles.smallFont}`}>
                  {
                  redundantList.map(([project, profession, file, floors]: any, idx: number) => (
                    // <div className={styles.smallFont} key={idx}>
                    //   <Tag color="blue">{profession}</Tag> / {file}（所在楼层：
                    //   {Array.isArray(floors) ? floors.join("、") : "未知楼层"}）
                    // </div>

                    <React.Fragment key={idx}>
                            <Tag color="blue">{profession}</Tag>
                            <span>
                              {file}（所在楼层：
                              {Array.isArray(floors) ? floors.join("、") : "未知楼层"}）
                            </span>
                    </React.Fragment>
                  ))}
                  </div>
                )}
              </Panel>


              {/* 重复的专业类型 */}
              <Panel
                header={`重复的专业类型（${repeatedProfessionList.length} 项）`}
                key="repeatedProfession"
              >
                {repeatedProfessionList.length === 0 ? (
                  <div className={styles.smallFont}>✅ 无重复的专业类型</div>
                ) : (
                  <div className={`${styles.problemListGrid} ${styles.smallFont}`}>
                  {
                  repeatedProfessionList.map(
                    ([project, _, path, profession]: any, idx: number) => (

                      <React.Fragment key={idx}>
                            <Tag color="blue">{profession}</Tag>
                            <span>
                               / {path}
                            </span>
                          </React.Fragment>
                    )
                  )}
                  </div>
                )}
              </Panel>

              {/* 详细的缺少专业图纸 */}
              <Panel
                header={`缺失的图纸（${missingFiles.length} 项）`}
                key="missingFiles"
              >
                {missingFiles.length === 0 ? (
                  <div className={styles.smallFont}>✅ 无缺失的图纸</div>
                ) : (
                  <div className={`${styles.problemListGrid} ${styles.smallFont}`}>
                  {
                    missingFiles.map(
                    ([level, detail]: any, idx: number) => (
                      <React.Fragment key={idx}>
                            <Tag color="blue">第{level}层</Tag> 
                            <span>缺少 {detail}</span>
                          </React.Fragment>
                    )
                  )}
                  </div>
                )}
              </Panel>




            </Collapse>
          </Panel>

          <Panel header="文件内质量检查 " key="intra">
              <Collapse>
                  {Object.entries(intraCheckProblems).map(([checkKey, files]) => {
                      const displayName = CHECK_ITEM_MAP[checkKey] || checkKey;
                      return (
                          <Panel header={`${displayName}（${files.length} 个）`} key={checkKey}>
                    {files.length === 0 ? (
                      <div className={styles.smallFont}>✅ 无此类问题图纸</div>
                    ) : (
                      <div className={`${styles.problemListGrid} ${styles.smallFont}`}>
                        {files.map((file, idx) => (
                        <React.Fragment key={idx}>
                          <Tag color="orange">{file.profession}</Tag>
                          <span>
                            {formatPath(file.filePath)}
                            {file.detail ? `（详情：${file.detail}）` : ''}
                          </span>
                        </React.Fragment>
                      ))}
                      </div>
                    )}
                          </Panel>
                      );
                  })}
              </Collapse>
          </Panel>


          
        </Collapse>
      </div>
    </div>
  );
}

export default ProjectCheckerViewer;
